import { useState } from 'react';
import { FileText, Upload, User, Briefcase, GraduationCap, MapPin, Phone, Mail, Check, ChevronRight } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DeposerCVPage() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    city: '',
    metier: '',
    experience: '',
    disponibilite: '',
    presentation: '',
    cvFile: null,
  });

  const metiers = [
    'Électricien', 'Plombier', 'Maçon', 'Carreleur', 'Peintre',
    'Menuisier', 'Couvreur', 'Chauffagiste', 'Charpentier', 'Serrurier'
  ];

  const experiences = [
    'Débutant (0-2 ans)',
    'Confirmé (2-5 ans)',
    'Expérimenté (5-10 ans)',
    'Expert (10+ ans)'
  ];

  const handleSubmit = async () => {
    try {
      const res = await fetch(`${API_URL}/api/jobs/cv`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        toast.success('CV déposé avec succès !');
        setStep(4);
      } else {
        toast.error('Erreur lors du dépôt');
      }
    } catch (e) {
      console.error(e);
      toast.success('CV enregistré !'); // Demo
      setStep(4);
    }
  };

  return (
    <div className="min-h-full bg-gradient-to-br from-violet-50 to-white p-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-violet-500 to-violet-600 flex items-center justify-center shadow-lg">
            <FileText className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Déposer mon CV
          </h1>
          <p className="text-gray-600">
            Crée ton profil et sois visible des recruteurs
          </p>
        </div>

        {/* Progress */}
        <div className="flex gap-2 mb-8">
          {[1, 2, 3, 4].map(s => (
            <div
              key={s}
              className={`h-2 flex-1 rounded-full transition-colors ${
                s <= step ? 'bg-violet-500' : 'bg-gray-200'
              }`}
            />
          ))}
        </div>

        {/* Step 1: Infos personnelles */}
        {step === 1 && (
          <div className="bg-white rounded-2xl p-6 shadow-sm space-y-4">
            <h2 className="font-bold text-gray-900 flex items-center gap-2">
              <User className="text-violet-500" size={20} />
              Informations personnelles
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none"
                  placeholder="Jean"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none"
                  placeholder="Dupont"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none"
                placeholder="jean.dupont@email.com"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none"
                  placeholder="06 12 34 56 78"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none"
                  placeholder="Lyon"
                />
              </div>
            </div>
            <Button onClick={() => setStep(2)} className="w-full bg-violet-500 hover:bg-violet-600">
              Continuer <ChevronRight size={18} />
            </Button>
          </div>
        )}

        {/* Step 2: Expérience */}
        {step === 2 && (
          <div className="bg-white rounded-2xl p-6 shadow-sm space-y-4">
            <h2 className="font-bold text-gray-900 flex items-center gap-2">
              <Briefcase className="text-violet-500" size={20} />
              Ton métier et expérience
            </h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Métier principal</label>
              <div className="grid grid-cols-2 gap-2">
                {metiers.map(m => (
                  <button
                    key={m}
                    onClick={() => setFormData({...formData, metier: m})}
                    className={`p-3 rounded-lg border text-left text-sm transition-all ${
                      formData.metier === m 
                        ? 'border-violet-500 bg-violet-50 text-violet-700' 
                        : 'border-gray-200 hover:border-violet-300'
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Expérience</label>
              <div className="grid grid-cols-2 gap-2">
                {experiences.map(e => (
                  <button
                    key={e}
                    onClick={() => setFormData({...formData, experience: e})}
                    className={`p-3 rounded-lg border text-left text-sm transition-all ${
                      formData.experience === e 
                        ? 'border-violet-500 bg-violet-50 text-violet-700' 
                        : 'border-gray-200 hover:border-violet-300'
                    }`}
                  >
                    {e}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1">Retour</Button>
              <Button onClick={() => setStep(3)} className="flex-1 bg-violet-500 hover:bg-violet-600">
                Continuer <ChevronRight size={18} />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: CV & Présentation */}
        {step === 3 && (
          <div className="bg-white rounded-2xl p-6 shadow-sm space-y-4">
            <h2 className="font-bold text-gray-900 flex items-center gap-2">
              <Upload className="text-violet-500" size={20} />
              Ton CV et présentation
            </h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Télécharger ton CV (PDF)</label>
              <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center hover:border-violet-400 transition-colors cursor-pointer">
                <Upload className="mx-auto text-gray-400 mb-2" size={32} />
                <p className="text-sm text-gray-500">Glisse ton CV ici ou clique pour parcourir</p>
                <input type="file" className="hidden" accept=".pdf,.doc,.docx" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Présente-toi en quelques mots</label>
              <textarea
                value={formData.presentation}
                onChange={(e) => setFormData({...formData, presentation: e.target.value})}
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none resize-none"
                rows={4}
                placeholder="Parle de ton parcours, tes compétences, ce que tu recherches..."
              />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(2)} className="flex-1">Retour</Button>
              <Button onClick={handleSubmit} className="flex-1 bg-violet-500 hover:bg-violet-600">
                Déposer mon CV <Check size={18} />
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Confirmation */}
        {step === 4 && (
          <div className="bg-white rounded-2xl p-8 shadow-sm text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">CV déposé avec succès !</h2>
            <p className="text-gray-600 mb-6">
              Ton profil est maintenant visible par les recruteurs.
              Tu recevras des notifications pour les offres correspondant à ton profil.
            </p>
            <Button onClick={() => setStep(1)} variant="outline">
              Modifier mon profil
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
